import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Trash2, Image as ImageIcon, RefreshCcw } from "lucide-react";
import { api } from "@/lib/api";

const CATEGORIES = ["Cake Pops", "Party Sets", "Seasonal Treats", "Custom Themes"];

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

export default function GalleryManager() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState(CATEGORIES[0]);
  const [title, setTitle] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/gallery");
      setItems(data || []);
    } catch {
      toast.error("Failed to load gallery");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    let uploaded = 0;
    for (const f of files) {
      if (!f.type.startsWith("image/")) { toast.error(`Skipped ${f.name}: not an image`); continue; }
      if (f.size > 4 * 1024 * 1024) { toast.error(`Skipped ${f.name}: over 4MB`); continue; }
      try {
        const data_url = await fileToDataUrl(f);
        await api.post("/gallery", { category, title: title || f.name.replace(/\.[^.]+$/, ""), data_url });
        uploaded += 1;
      } catch (err) {
        toast.error(`Upload failed for ${f.name}`);
      }
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
    if (uploaded) { toast.success(`Uploaded ${uploaded} photo${uploaded > 1 ? "s" : ""}`); setTitle(""); load(); }
  };

  const remove = async (id) => {
    if (!confirm("Delete this photo from the gallery?")) return;
    try {
      await api.delete(`/gallery/${id}`);
      setItems((prev) => prev.filter((i) => i.id !== id));
      toast.success("Removed");
    } catch {
      toast.error("Could not remove");
    }
  };

  return (
    <div className="mt-10 card-soft p-6 md:p-8" data-testid="gallery-manager">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <span className="h-11 w-11 grid place-items-center rounded-full bg-sakura-200 text-sakura-500"><ImageIcon className="h-5 w-5" /></span>
          <div>
            <h3 className="font-serif text-2xl text-sakura-ink">Gallery</h3>
            <p className="text-sm text-sakura-soft">Upload photos here — they appear on the public Gallery page instantly.</p>
          </div>
        </div>
        <button onClick={load} className="btn-ghost" data-testid="gallery-refresh"><RefreshCcw className="h-4 w-4" /> Refresh</button>
      </div>

      <div className="mt-5 grid md:grid-cols-3 gap-3 items-end">
        <label className="block">
          <span className="text-sm font-semibold text-sakura-ink">Category</span>
          <select className="input-soft mt-1" value={category} onChange={(e) => setCategory(e.target.value)} data-testid="gallery-category">
            {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="text-sm font-semibold text-sakura-ink">Title (optional)</span>
          <input className="input-soft mt-1" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g., Birthday blush pops" data-testid="gallery-title" />
        </label>
        <div>
          <input ref={fileRef} id="gallery-file-input" type="file" multiple accept="image/*" className="hidden" onChange={onFiles} data-testid="gallery-file-input" />
          <label htmlFor="gallery-file-input" className={`btn-primary w-full cursor-pointer ${uploading ? "opacity-60 pointer-events-none" : ""}`} data-testid="gallery-upload-btn">
            <Upload className="h-4 w-4" /> {uploading ? "Uploading…" : "Upload photo(s)"}
          </label>
          <p className="text-[11px] text-sakura-soft mt-1">JPG / PNG / WEBP · up to 4MB each</p>
        </div>
      </div>

      <div className="mt-6">
        {loading ? (
          <div className="py-10 text-center text-sakura-soft">Loading…</div>
        ) : items.length === 0 ? (
          <div className="py-10 text-center text-sakura-soft">No photos yet. Upload your first one above.</div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="gallery-items">
            {items.map((it) => (
              <div key={it.id} className="relative group rounded-2xl overflow-hidden border border-sakura-200 bg-sakura-50">
                <img src={it.data_url} alt={it.title || it.category} className="w-full aspect-square object-cover" loading="lazy" />
                <div className="absolute inset-x-0 bottom-0 p-2 bg-gradient-to-t from-black/60 to-transparent text-white text-xs font-semibold flex items-center justify-between">
                  <span className="truncate">{it.category}</span>
                  <button onClick={() => remove(it.id)} aria-label="Delete" className="h-7 w-7 grid place-items-center rounded-full bg-white/90 text-sakura-500 hover:bg-white" data-testid={`gallery-delete-${it.id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
